import type { FlashOfferRoomDraft } from '../RoomsStep/utils';

export interface RoomRateRoom {
  // A real room type id (existing property rooms) or a room draft's stable draftId (rooms not
  // yet created) -- real ids only exist once Submission has actually created the room.
  id: string;
  name: string;
  hasRatePlan: boolean;
}

type RoomRateSource = Pick<App.RoomType, 'id' | 'name'> & {
  room_rates?: Array<{ rate_plan?: { name?: string | null } | null }>;
};

type RatePlanDraftSource = {
  draftId: string;
  ratePlanName: string;
};

type BuildRoomRateRoomsParams = {
  ratePlanDrafts?: Array<RatePlanDraftSource>;
  roomDrafts: Array<FlashOfferRoomDraft>;
  roomTypes: Array<RoomRateSource>;
};

function roomHasAllRatePlanNames(
  roomRates: Array<{ rate_plan?: { name?: string | null } | null }>,
  ratePlanNames: Array<string>,
) {
  return ratePlanNames.every((ratePlanName) => roomRates.some((roomRate) => roomRate.rate_plan?.name === ratePlanName));
}

export function buildRoomRateRooms({ ratePlanDrafts = [], roomDrafts, roomTypes }: BuildRoomRateRoomsParams) {
  const roomsById = new Map<string, RoomRateRoom>();
  // Matched by name, not id -- the rate plans being offered here are still local drafts with no
  // real id of their own until Submission creates them.
  const ratePlanNames = ratePlanDrafts.map((draft) => draft.ratePlanName).filter(Boolean);

  roomTypes.forEach((roomType) => {
    if (!roomType.id) {
      return;
    }

    roomsById.set(roomType.id, {
      id: roomType.id,
      name: roomType.name || roomType.id,
      hasRatePlan: ratePlanNames.length > 0 && roomHasAllRatePlanNames(roomType.room_rates ?? [], ratePlanNames),
    });
  });

  roomDrafts.forEach((roomDraft) => {
    const roomKey = roomDraft.id || roomDraft.draftId;

    if (roomsById.has(roomKey)) {
      return;
    }

    roomsById.set(roomKey, {
      id: roomKey,
      name: roomDraft.existingRoomName || roomDraft.name || roomKey,
      hasRatePlan: false,
    });
  });

  return Array.from(roomsById.values());
}

/** Keeps only the rate plan ids that match a rate plan draft, and only the rooms left with one. */
export function pruneSelectionToKnownRatePlans(
  selectedRoomRatePlanIds: Record<string, Array<string>>,
  knownRatePlanDraftIds: Array<string>,
) {
  const known = new Set(knownRatePlanDraftIds);

  return Object.fromEntries(
    Object.entries(selectedRoomRatePlanIds).flatMap(([roomKey, ratePlanIds]) => {
      const knownRatePlanIds = ratePlanIds.filter((ratePlanId) => known.has(ratePlanId));

      return knownRatePlanIds.length > 0 ? [[roomKey, knownRatePlanIds]] : [];
    }),
  );
}

/** Drops selections keyed by a room that no longer exists (a removed draft, or another property's room). */
export function pruneSelectionToKnownRooms(
  selectedRoomRatePlanIds: Record<string, Array<string>>,
  knownRoomIds: Array<string>,
) {
  const known = new Set(knownRoomIds);

  return Object.fromEntries(Object.entries(selectedRoomRatePlanIds).filter(([roomKey]) => known.has(roomKey)));
}
